// Gestion des annonces globales (admin) : création, activation, suppression
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NIVEAUX_ANNONCE } from "@/lib/constants";
import { formatJour } from "@/lib/dates";

const BADGES = {
  info: "bg-success/20 text-success",
  important: "bg-primary/20 text-primary",
  urgent: "bg-destructive text-destructive-foreground",
};

export default function Announcements() {
  const { user } = useAuth();
  const [annonces, setAnnonces] = useState([]);
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [form, setForm] = useState({ titre: "", message: "", niveau: "info", expire_le: "" });

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements");
      setAnnonces(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { if (user.role === "admin") charger(); }, [charger, user.role]);

  if (user.role !== "admin") {
    return <p className="text-center py-24 text-muted-foreground">Cette page est réservée aux administrateurs.</p>;
  }

  const creer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", { ...form, expire_le: form.expire_le || null });
      toast.success("Annonce publiée — elle s'affiche sur le dashboard de tous");
      setDialogOuvert(false);
      setForm({ titre: "", message: "", niveau: "info", expire_le: "" });
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const basculerActif = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { actif: !a.actif });
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Annonce supprimée");
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold">Annonces</h1>
          <p className="text-muted-foreground text-sm mt-1">Bannières globales affichées sur le dashboard de tous</p>
        </div>
        <Button onClick={() => setDialogOuvert(true)} className="rounded-full gap-1.5" data-testid="new-announcement-button">
          <Plus className="h-4 w-4" /> Nouvelle annonce
        </Button>
      </div>

      <div className="space-y-3">
        {annonces.map((a) => (
          <div key={a.id} className="flex items-start gap-4 bg-card rounded-2xl border border-border px-5 py-4 carte-douce" data-testid={`announcement-row-${a.id}`}>
            <Megaphone className="h-5 w-5 text-muted-foreground mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{a.titre}</p>
                <Badge className={`${BADGES[a.niveau]} border-0`}>{NIVEAUX_ANNONCE[a.niveau]?.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{a.message}</p>
              <p className="text-xs text-muted-foreground/70 mt-1.5">
                Créée le {formatJour(a.created_at)}
                {a.expire_le && ` · expire le ${formatJour(a.expire_le)}`}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={a.actif} onCheckedChange={() => basculerActif(a)} data-testid={`announcement-toggle-${a.id}`} />
              <button onClick={() => supprimer(a.id)} className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors" data-testid={`announcement-delete-${a.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {annonces.length === 0 && <p className="text-sm text-muted-foreground text-center py-12">Aucune annonce.</p>}
      </div>

      <Dialog open={dialogOuvert} onOpenChange={setDialogOuvert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Nouvelle annonce</DialogTitle>
            <DialogDescription>Elle apparaîtra en bannière sur le dashboard de toute l'équipe.</DialogDescription>
          </DialogHeader>
          <form onSubmit={creer} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="rounded-xl mt-1.5" data-testid="announcement-titre-input" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea rows={3} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl mt-1.5" data-testid="announcement-message-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Niveau</Label>
                <Select value={form.niveau} onValueChange={(v) => setForm({ ...form, niveau: v })}>
                  <SelectTrigger className="rounded-xl mt-1.5" data-testid="announcement-niveau-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(NIVEAUX_ANNONCE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date d'expiration</Label>
                <Input type="date" value={form.expire_le} onChange={(e) => setForm({ ...form, expire_le: e.target.value })} className="rounded-xl mt-1.5" data-testid="announcement-expire-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-full" data-testid="announcement-create-button">Publier</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
